import type { TransactionSummary, Transaction } from '../types';

interface DataTableProps {
  summaries: TransactionSummary[];
  onRowClick?: (aptName: string) => void;
  selectedApt?: string;
}

export default function DataTable({ summaries, onRowClick, selectedApt }: DataTableProps) {
  const formatPrice = (price: number): string => {
    const billion = Math.floor(price / 10000);
    const million = price % 10000;
    
    if (billion > 0) {
      return `${billion}억 ${million > 0 ? million + '만' : ''}`;
    }
    return `${million}만`;
  };

  const formatYear = (year: number): string => {
    const currentYear = new Date().getFullYear();
    const age = currentYear - year;
    return `${year}년 (${age}년차)`;
  };

  return (
    <div className="card overflow-x-auto">
      <h2 className="text-lg font-semibold mb-4">📊 아파트별 시세 요약</h2>
      
      <table className="table">
        <thead>
          <tr>
            <th>아파트명</th>
            <th>준공년도</th>
            <th>평균매매가</th>
            <th>최저가</th>
            <th>최고가</th>
            <th>평당가</th>
            <th>거래건수</th>
          </tr>
        </thead>
        <tbody>
          {summaries.map((summary) => (
            <tr 
              key={summary.aptName}
              className={`cursor-pointer ${selectedApt === summary.aptName ? 'bg-blue-50' : ''}`}
              onClick={() => onRowClick?.(summary.aptName)}
            >
              <td className="font-medium">{summary.aptName}</td>
              <td>{formatYear(summary.buildYear)}</td>
              <td className="font-semibold text-primary-700">{formatPrice(summary.avgPrice)}</td>
              <td>{formatPrice(summary.minPrice)}</td>
              <td>{formatPrice(summary.maxPrice)}</td>
              <td>{summary.avgPricePerPyeong.toLocaleString()}만</td>
              <td className="text-center">{summary.transactionCount}건</td>
            </tr>
          ))}
        </tbody>
      </table>

      {summaries.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          데이터가 없습니다. 지역을 선택해주세요.
        </div>
      )}
    </div>
  );
}

// 거래 내역 상세 테이블
interface TransactionTableProps {
  transactions: Transaction[];
}

export function TransactionTable({ transactions }: TransactionTableProps) {
  const formatPrice = (price: number): string => {
    const billion = Math.floor(price / 10000);
    const million = price % 10000;
    
    if (billion > 0) {
      return `${billion}억 ${million > 0 ? million + '만' : ''}`;
    }
    return `${million}만`;
  };

  const sortedTransactions = [...transactions].sort((a, b) => {
    const dateA = a.dealDate.replace(/\./g, '');
    const dateB = b.dealDate.replace(/\./g, '');
    return dateB.localeCompare(dateA);
  });

  return (
    <div className="card overflow-x-auto">
      <h2 className="text-lg font-semibold mb-4">📋 최근 실거래가 내역</h2>
      
      <table className="table">
        <thead>
          <tr>
            <th>아파트명</th>
            <th>거래일자</th>
            <th>금액</th>
            <th>전용면적</th>
            <th>평수</th>
            <th>층</th>
          </tr>
        </thead>
        <tbody>
          {sortedTransactions.slice(0, 20).map((t, index) => (
            <tr key={index}>
              <td className="font-medium">{t.aptName}</td>
              <td>{t.dealDate}</td>
              <td className="font-semibold text-primary-700">{formatPrice(t.dealAmount)}</td>
              <td>{t.area.toFixed(2)}㎡</td>
              <td>{(t.area / 3.3058).toFixed(1)}평</td>
              <td className="text-center">{t.floor}층</td>
            </tr>
          ))}
        </tbody>
      </table>

      {transactions.length > 20 && (
        <p className="text-sm text-gray-500 mt-2 text-center">
          최근 20건만 표시 (전체 {transactions.length}건)
        </p>
      )}
    </div>
  );
}